import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TrackByFunction,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { RxFor } from '@rx-angular/template/for';
import {
  Absence,
  AssignmentStack,
  TimelineShift,
  UserTimelines,
} from '@wilson/interfaces';
import {
  AssignShiftDropZoneCellViewComponent,
  CellViewAssignmentCardComponent,
  ShiftMultiSelectHelperService,
  TimelineCellHighlightComponent,
  TimelineCellHighlightService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { ShiftTimelinePipesModule } from '@wilson/shift-timeline/pipes';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  getDateFromPixelPosition,
  TimelineHelperService,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  AssignedRegionVisibilityStateService,
} from '@wilson/shift-timeline/state';
import {
  NzContextMenuService,
  NzDropdownMenuComponent,
  NzDropDownModule,
} from 'ng-zorro-antd/dropdown';
import { ShiftTransferHandlerV2Service } from '../../components/add-transfer-modal/shift-transfer-handler-v2.service';
import { MoveAbsenceDropZoneHelperService } from '../../components/move-absence-drop-zone/move-absence-drop-zone-helper.service';
import { NewAbsenceDropZoneCellViewComponent } from '../../components/new-absence-drop-zone-cell-view/new-absence-drop-zone-cell-view.component';
import { AbsenceCardPosition } from '../../interfaces/shift-timeline-custom-types';
import { AssignShiftDropZoneCellViewHelperService } from '../../services/assign-shift-drop-zone-cell-view-helper.service';
import { ShiftDrawerService } from '../../services/shift-drawer.service';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { TimelineShiftAssignService } from '../../services/timeline-shift-assign.service';
import { TimelineShiftDragAndDropDirectiveHelperService } from '../../services/timeline-shift-drag-and-drop-directive-helper.service';
import { AssignShiftDropZoneHelperService } from '../assign-shift-drop-zone-v2/new-shift-drop-zone-helper.service';
import { GroupAssignmentHelperService } from '../group-assignment-confirmation-modal/group-assignment-helper.service';
import { NewShiftDropZoneCellViewComponent } from '../new-shift-drop-zone-cell-view/new-shift-drop-zone-cell-view.component';
import { ShiftTemplateDropZoneV2Component } from '../shift-template-drop-zone-v2/shift-template-drop-zone-v2.component';
import { TimelineUserRowContextMenuWrapperComponent } from '../timeline-user-row-shifts-v2/timeline-user-row-context-menu-wrapper/timeline-user-row-context-menu-wrapper.component';
import { TimelineUserRowContextMenuWrapperService } from '../timeline-user-row-shifts-v2/timeline-user-row-context-menu-wrapper/timeline-user-row-context-menu-wrapper.service';
import { ContainsIdPipe } from './contains-id.pipe';
import { BehaviorSubject, debounceTime, pairwise, Subscription } from 'rxjs';
import { AbsenceDrawerHelperService } from '@wilson/non-domain-specific/absence';
import {
  wilsonCancelIdleCallback,
  wilsonRequestIdleCallback,
} from '@wilson/utils';

@Component({
  selector: 'wilson-timeline-cell-view-user-row-assignments',
  templateUrl: './timeline-cell-view-user-row-assignments.component.html',
  styleUrls: ['./timeline-cell-view-user-row-assignments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ShiftTransferHandlerV2Service,
    MoveAbsenceDropZoneHelperService,
    GroupAssignmentHelperService,
    AssignShiftDropZoneHelperService,
    TimelineShiftAssignService,
    AssignShiftDropZoneCellViewHelperService,
  ],
  imports: [
    CommonModule,
    ShiftTimelinePipesModule,
    RxFor,
    TimelineUserRowContextMenuWrapperComponent,
    NzDropDownModule,
    TimelineCellHighlightComponent,
    NewShiftDropZoneCellViewComponent,
    NewAbsenceDropZoneCellViewComponent,
    ShiftTemplateDropZoneV2Component,
    AssignShiftDropZoneCellViewComponent,
    CellViewAssignmentCardComponent,
    ContainsIdPipe,
  ],
})
export class TimelineCellViewUserRowAssignmentsComponent
  implements OnInit, OnDestroy
{
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineUserRowContextMenuWrapperService = inject(
    TimelineUserRowContextMenuWrapperService,
  );
  private readonly nzContextMenuService = inject(NzContextMenuService);
  private readonly store = inject(Store);
  private readonly timelineShiftDragAndDropDirectiveHelperService = inject(
    TimelineShiftDragAndDropDirectiveHelperService,
  );
  protected readonly timelineCellHighlightService = inject(
    TimelineCellHighlightService,
  );
  protected readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  protected readonly dragAndDropModeService = inject(DragAndDropModeService);
  protected readonly timelineHelperService = inject(TimelineHelperService);
  protected readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  protected readonly assignShiftDropZoneCellViewHelperService = inject(
    AssignShiftDropZoneCellViewHelperService,
  );
  readonly absenceDrawerHelperService = inject(AbsenceDrawerHelperService);
  readonly moveAbsenceDropZoneHelperService = inject(
    MoveAbsenceDropZoneHelperService,
  );
  readonly shiftDrawerService = inject(ShiftDrawerService);
  private assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  protected userDataRowsSubject = new BehaviorSubject<AssignmentStack[]>([]);

  @Input() user!: UserTimelines;
  @Input() set assignmentStacksData(value: AssignmentStack[]) {
    this.userDataRowsSubject.next(value);
  }
  @Input() defaultRowHeight!: number | null;
  @Input() parentElementRef!: HTMLElement;
  @Input() isTooltipVisible!: boolean | null;
  @Input() isLastUserRow = false;

  private idleCallbackId: ReturnType<typeof wilsonRequestIdleCallback> | null =
    null;
  private subscriptions = new Subscription();

  trackByAssignmentStack: TrackByFunction<AssignmentStack> = (
    _: number,
    assignmentStack: AssignmentStack,
  ) => {
    return (
      assignmentStack.date.getTime() +
      assignmentStack.shifts.map((shift) => shift.id).join(',') +
      assignmentStack.absences.map((absence) => absence.id).join(',')
    );
  };

  protected pixelWidthPerDay$ = this.store.select(
    ShiftTimelineSettingsState.pixelWidthPerDay(),
  );
  protected isShiftDrawerVisible$ = this.shiftDrawerService.isDrawerVisible$;

  ngOnInit(): void {
    this.subscriptions.add(
      this.userDataRowsSubject
        .pipe(debounceTime(10), pairwise())
        .subscribe(([previousUserDataStack, currentUserDataStack]) => {
          if (this.idleCallbackId) {
            wilsonCancelIdleCallback(this.idleCallbackId);
          }
          this.idleCallbackId = wilsonRequestIdleCallback(() => {
            this.assignedRegionVisibilityStateService.updateCellViewTimelineShiftsVisibilityForUser(
              currentUserDataStack,
              previousUserDataStack,
              this.user.id as string,
            );
          });
        }),
    );
  }

  openAbsenceDrawer(absencePosition: AbsenceCardPosition): void {
    this.shiftMultiSelectHelperService.clearSelection();
    this.timelineCommonOperationsService.openAbsenceDrawer(
      absencePosition.left,
      absencePosition.right,
      absencePosition.id,
    );
  }

  public startAbsenceDrag(event: {
    absence: Absence & { id: string; isHighlighted: boolean };
    hasAbsenceUpdatePermissions: boolean;
  }): void {
    if (event.hasAbsenceUpdatePermissions) {
      const isDragStarted = this.moveAbsenceDropZoneHelperService.startDrag(
        event.absence,
      );
      if (isDragStarted) {
        this.moveAbsenceDropZoneHelperService.initiateDragAndDrop(null, null);
      }
    } else {
      this.timelineHelperService.informUserHasNoPermissionsToMoveEntity();
    }
  }

  contextMenu(
    event: MouseEvent,
    menu: NzDropdownMenuComponent,
    rowContainer: HTMLDivElement,
  ): void {
    if (this.user.id && event.target === rowContainer) {
      const shiftStartDate = getDateFromPixelPosition({
        pixelPosition: event.offsetX,
        timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
      });
      this.timelineCellHighlightService.setCellToHighlight(
        this.user.id,
        shiftStartDate.toISOString(),
      );

      const menuRef =
        this.timelineUserRowContextMenuWrapperService.createDropDown({
          menuService: this.nzContextMenuService,
          mouseEvent: event,
          menu,
          userId: this.user.id,
        });
      menuRef.onDestroy(() => {
        this.timelineCellHighlightService.removeCellFromHighlight(
          this.user.id as string,
          shiftStartDate.toISOString(),
        );
      });
    }
  }

  handleShiftDragStarted(event: {
    shift: TimelineShift;
    isAssignmentMode: boolean;
    hasShiftUpdatePermissions: boolean;
  }): void {
    this.timelineShiftDragAndDropDirectiveHelperService.setIsTooltipVisible(
      false,
    );
    if (event.hasShiftUpdatePermissions) {
      this.shiftMultiSelectHelperService.clearSelection();
      this.timelineShiftDragAndDropDirectiveHelperService.initiateCellViewDragAndDrop(
        !!event.isAssignmentMode,
      );

      setTimeout(() => {
        this.dragAndDropModeService.setDraggingShiftV2(event.shift);
      });
    } else {
      this.timelineHelperService.informUserHasNoPermissionsToMoveEntity();
      setTimeout(() => {
        this.timelineShiftDragAndDropDirectiveHelperService.setIsTooltipVisible(
          true,
        );
      }, 600);
    }
  }

  handleShiftDragEnded(): void {
    this.timelineShiftDragAndDropDirectiveHelperService.handleCellViewDragEnd();
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    wilsonRequestIdleCallback(() => {
      this.assignedRegionVisibilityStateService.clearAllShiftVisibilityRecordForUser(
        this.user.id as string,
      );
    });
  }
}
